import { useDebounce } from '@neo/hooks/use-debounce'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type {
  CashfreeOrderCreatePayload,
  CashfreePayPayload,
  CashfreeSubscriptionCreatePayload,
  CashfreeVBankCreatePayload,
  ConversionRate,
  RazorpayOrderCreatePayload,
  RazorpayPayWithTokenPayload,
  RazorpayVBankCreatePayload,
  RazorpayVerifyPayload,
} from 'v2/pages/billing/types'
import { apiConsoleService } from 'v2/shared/services/api-console-service'
import { billingService } from 'v2/shared/services/billing-service'
import { billingQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RInvoicesPayload } from 'v2/types/api/billing'
import { errorToast, successToast } from 'v2/utils/toasts'
import { Currency, GstStatus } from './billing.config'
import type { SendBlockedEmailPayload } from './types'
import { getAccountId, resolvePaymentAmounts, validateIndianZipCode } from './utils'

export const useAccountDetailsQuery = () => {
  const accountId = getAccountId()
  return useQuery({
    queryKey: billingQueryKeys().accountDetails(accountId),
    queryFn: async () => {
      const response = await billingService.getAccountDetails(accountId)
      return response.data
    },
    enabled: !!accountId,
  })
}
export const useConversionRate = (): ConversionRate => {
  const { data: accountDetails, isLoading: isAccountDetailsLoading } = useAccountDetailsQuery()
  return {
    conversionRate: accountDetails?.account?.conversion_rate || 1,
    currencyCode: accountDetails?.account?.currency || 'USD',
    isLoading: isAccountDetailsLoading,
  }
}
export const useBillingInvoicesQuery = (payload: RInvoicesPayload) => {
  const { conversionRate, isLoading: isAccountDetailsLoading } = useConversionRate()
  const { data: accountDetails } = useAccountDetailsQuery()
  const isIndian = accountDetails?.account?.currency === Currency.INR

  return useQuery({
    staleTime: 0,
    enabled: !isAccountDetailsLoading,
    queryKey: billingQueryKeys().invoices(payload, conversionRate, isIndian),
    queryFn: async () => {
      const response = isIndian
        ? await billingService.getCurrencyInvoices(payload)
        : await billingService.getInvoices(payload)
      const displayInvoices = response.invoices.filter(
        (invoice) => invoice.invoice_type === 'MONTHLY_INVOICE',
      )
      const invoiceResp = {
        ...response,
        invoices: displayInvoices.sort(
          (a, b) => dayjs(b.invoice_date).valueOf() - dayjs(a.invoice_date).valueOf(),
        ),
      }
      return invoiceResp
    },
  })
}

export const useBillingBalanceQuery = () => {
  const accountId = getAccountId()
  const { conversionRate, isLoading: isAccountDetailsLoading } = useConversionRate()

  return useQuery({
    staleTime: 0,
    // Only enable the query when we have a valid accountId, prevents unnecessary API calls when accountId is 0 or undefined
    enabled: !!accountId && !isAccountDetailsLoading,
    queryKey: billingQueryKeys().balance(accountId, conversionRate),
    queryFn: async () => {
      const response = await billingService.getBalance(accountId)
      return {
        ...response.data,
        amount: Number(response.data.amount || 0) * conversionRate,
        threshold_amount: Number(response.data.threshold_amount || 0) * conversionRate,
      }
    },
  })
}

export const useTransactionsQuery = (limit = 20, offset = 0) => {
  const accountId = getAccountId()

  return useQuery({
    staleTime: 0,
    enabled: !!accountId,
    queryKey: billingQueryKeys().transactions(accountId, limit, offset),
    queryFn: async () => {
      const response = await billingService.getTransactions(accountId, { limit, offset })

      // Each row carries the currency it was actually charged in, so normalise
      // per row rather than converting everything into the account's current one.
      return {
        ...response,
        payment_logs: response.payment_logs?.map(resolvePaymentAmounts) || [],
      }
    },
  })
}

/**
 * Retrieves receipt PDF details using an authorization ID and a receipt UUID.
 *
 * This hook utilizes useQuery to asynchronously fetch receipt PDF details from the billing service.
 * The query is executed only if a valid receipt UUID is provided.
 *
 * @param authId - The authorization identifier for validating the request.
 * @param uuid - The unique identifier for the receipt PDF; the query runs only when this is truthy.
 *
 * @returns A query object containing the receipt PDF details along with the query state.
 */
export function useReceiptPDFDetailsQuery(authId: string, uuid: string) {
  return useQuery({
    queryKey: billingQueryKeys().receiptPDFDetails(authId, uuid),
    queryFn: async () => {
      const response = await billingService.getReceiptPDFDetails(authId, uuid)
      return {
        ...response,
        receipt_details: resolvePaymentAmounts(response.receipt_details),
      }
    },
    // authId comes from the account-details cache, which is COLD when the receipt
    // opens in a fresh tab. Without this the query fired with authId '' — a request
    // that can only fail, and (until 404s stopped being retried) burned three
    // backoff rounds before the real authId arrived and refetched under a new key.
    enabled: !!uuid && !!authId,
  })
}

// export const useBillingInvoicesPDFQuery = (payload: RInvoicesPayload) =>
//   useQuery({
//     queryKey: ['billing-invoices-pdf', payload],
//     queryFn: async () => {
//       const response = await billingService.getInvoicesPDF(payload)
//       return response
//     },
//   }) //TODO

export const useBillingCardsQuery = () => {
  const accountId = getAccountId()
  return useQuery({
    staleTime: 0,
    enabled: !!accountId,
    queryKey: billingQueryKeys().cards(accountId),
    queryFn: async () => {
      const response = await billingService.getCards(accountId)
      // Sort cards so default card appears first
      const sortedRecords =
        response.records?.sort((a, b) => {
          if (a.default && !b.default) return -1
          if (!a.default && b.default) return 1
          return 0
        }) || []
      return sortedRecords
    },
  })
}

export const useBillingAddressQuery = () => {
  const accountId = getAccountId()
  return useQuery({
    staleTime: 0,
    enabled: !!accountId,
    queryKey: billingQueryKeys().address(accountId),
    queryFn: async () => {
      const response = await billingService.getAddress(accountId)
      return response.account_billing
    },
  })
}

export const useBillingRechargeDetailsQuery = () => {
  const accountId = getAccountId()
  const { conversionRate } = useConversionRate()
  return useQuery({
    staleTime: 0,
    enabled: !!accountId,
    queryKey: billingQueryKeys().rechargeDetails(accountId, conversionRate),
    queryFn: async () => {
      const response = await billingService.getRechargeDetails(accountId)
      return {
        ...response.details,
        current_month_total_payment:
          Number(response.details.current_month_total_payment || 0) * conversionRate,
      }
    },
  })
}

// Real month-to-date account spend, via the hodor plivo-analytics proxy straight
// to messaging-dashboard's /v1/usage/monthly-total. Returns the current-month
// total in the account's display currency. This is the correct source for
// "usage" — unlike recharge details, it is independent of top-ups.
//
// Previously this read meta.total_spend off /plivo-api/UsageSummary/ and threw
// the usage[] rows away. That endpoint derives its total from a full-window
// aggregate over every usage dimension, which exceeds the upstream statement
// timeout on accounts with many subaccounts — those accounts got a 502 and no
// figure at all. monthly-total answers the same question with five flat SUMs.
//
// The number is not identical: monthly-total is the usage-limit enforcement
// figure (no messaging surcharge, different billing-event charge types), which
// is what the spend limit is actually enforced against — the value this is
// rendered next to. See RUsageMonthlyTotal.
//
// The window is chosen upstream (first of month UTC through today), so no dates
// are sent. The usage-reports page still uses UsageSummary: it needs the rows.
export const useUsageSummaryQuery = () => {
  const accountId = getAccountId()
  const { conversionRate, currencyCode, isLoading: isRateLoading } = useConversionRate()
  const month = dayjs().startOf('month').format('YYYY-MM-DD')
  return useQuery({
    // staleTime 0 like the other billing queries — the account's query client
    // keeps data fresh for a minute with focus-refetch disabled, and this value
    // is date-sensitive, so refetch on every mount instead of time-travelling.
    staleTime: 0,
    enabled: !!accountId && !isRateLoading,
    queryKey: billingQueryKeys().usageMonthlyTotal(accountId, month, conversionRate),
    queryFn: async () => {
      const response = await apiConsoleService.getUsageMonthlyTotal()
      const totalSpend = Math.max(0, Number(response?.total_spend ?? 0))
      // The usage limit is USD-denominated and displayed ×conversionRate. Match
      // units: if the upstream already reports the display currency, use as-is;
      // otherwise (USD) convert with the same rate.
      return response?.currency && response.currency === currencyCode
        ? totalSpend
        : totalSpend * conversionRate
    },
  })
}

export const useBalanceAlertsQuery = () => {
  const accountId = getAccountId()
  const { conversionRate } = useConversionRate()
  return useQuery({
    staleTime: 0,
    enabled: !!accountId,
    queryKey: billingQueryKeys().balanceAlerts(accountId, conversionRate),
    queryFn: async () => {
      const response = await billingService.getBalanceAlerts(accountId)
      return response.balance_threshold_details.map((detail) => ({
        ...detail,
        threshold_amount: Number(detail.threshold_amount || 0) * conversionRate,
      }))
    },
  })
}

export const useBillingEmailQuery = () => {
  const accountId = getAccountId()
  return useQuery({
    staleTime: 0,
    enabled: !!accountId,
    queryKey: billingQueryKeys().billingEmail(accountId),
    queryFn: async () => {
      const response = await billingService.getBillingEmail(accountId)
      return response.billing_email
    },
  })
}

export const useGstValidationQuery = (gstNumber: string) => {
  const debouncedGstNumber = useDebounce(gstNumber, 1000)

  return useQuery({
    queryKey: billingQueryKeys().gstValidation(debouncedGstNumber),
    queryFn: async () => {
      if (!debouncedGstNumber || debouncedGstNumber.length !== 15)
        return { isValid: false, gstinStatus: null }
      const response = await billingService.verifyGSTNumber({
        task_id: `gst_verify_${Date.now()}`,
        group_id: `gst_batch_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`,
        data: {
          gstin: debouncedGstNumber,
          filling_status: true,
        },
      })
      const gstinStatus = response.result?.source_output?.gstin_status
      return {
        isValid: gstinStatus === GstStatus.ACTIVE,
        gstinStatus: gstinStatus || null,
      }
    },
    enabled: Boolean(debouncedGstNumber) && debouncedGstNumber.length === 15,
  })
}
export const useSendBlockedEmailQuery = () =>
  useMutation({
    mutationFn: async (data: SendBlockedEmailPayload) => {
      const response = await billingService.sendBlockedEmail(data)
      return response
    },
  })

export const useZipCodeValidationQuery = (zipCode: string, country: string, state: string) => {
  const debouncedZipCode = useDebounce(zipCode, 1000)

  return useQuery({
    queryKey: billingQueryKeys().zipCodeValidation(debouncedZipCode, country, state),
    queryFn: async () => {
      if (!debouncedZipCode) return { status: false, errorMsg: 'ZIP code is required' }
      if (debouncedZipCode.length === 6) {
        return validateIndianZipCode(debouncedZipCode, state)
      }

      return { status: false, errorMsg: 'ZIP code must be 6 digits' }
    },
    enabled: Boolean(debouncedZipCode) && country === 'IN' && debouncedZipCode.length === 6,
  })
}

export const usePricingQuery = (countryIso: string, enabled = true) => {
  return useQuery({
    queryKey: billingQueryKeys().pricing(countryIso),
    queryFn: async () => {
      const response = await billingService.getPricing(countryIso)
      return response.data
    },
    enabled: Boolean(countryIso) && enabled,
  })
}

export const useZentrunkPricingQuery = (countryIso: string, enabled: boolean) => {
  return useQuery({
    queryKey: billingQueryKeys().zentrunkPricing(countryIso),
    queryFn: async () => {
      const response = await billingService.getZentrunkPricing(countryIso)
      return response.data
    },
    enabled: Boolean(countryIso) && enabled,
  })
}

// ============================================
// ACH / Bank Direct Debit Queries
// ============================================

export const useBankAccountQuery = () => {
  const accountId = getAccountId()
  return useQuery({
    staleTime: 0,
    enabled: !!accountId,
    queryKey: billingQueryKeys().bankAccount(accountId),
    queryFn: async () => {
      const response = await billingService.getBankAccount(accountId)
      return response.account_bank_mappings?.[0] || null
    },
    placeholderData: undefined,
  })
}

export const useACHStatusQuery = () => {
  const accountId = getAccountId()
  return useQuery({
    staleTime: 0,
    enabled: !!accountId,
    queryKey: billingQueryKeys().achStatus(accountId),
    queryFn: async () => {
      try {
        const response = await billingService.getACHStatus(accountId)
        return response.enabled
      } catch (error: unknown) {
        // 404 means ACH not configured, treat as disabled
        const status =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { status?: number } }).response?.status
            : undefined
        if (status === 404) {
          return false
        }
        throw error
      }
    },
  })
}

// ============================================
// USD Bank Transfer Queries
// ============================================

export const useWireTransferDetailsQuery = () => {
  const accountId = getAccountId()
  return useQuery({
    staleTime: 0,
    enabled: !!accountId,
    queryKey: billingQueryKeys().wireTransferDetails(accountId),
    queryFn: async () => {
      const response = await billingService.getWireTransferDetails(accountId)
      const abaAddress = response.bank_details?.bank_transfer?.financial_addresses?.find(
        (addr) => addr.type === 'aba',
      )
      return abaAddress?.aba ?? null
    },
  })
}

export const useAutoDebitSchedulesQuery = (bankMappingId: number | undefined) => {
  const accountId = getAccountId()
  return useQuery({
    staleTime: 0,
    enabled: !!accountId && !!bankMappingId,
    queryKey: billingQueryKeys().autoDebitSchedules(accountId, bankMappingId || 0),
    queryFn: async () => {
      if (!bankMappingId) return []
      const response = await billingService.getAutoDebitSchedules(accountId, bankMappingId)
      return response.objects || []
    },
  })
}

// ============================================
// Cashfree (India PSP) Queries
// ============================================

export const useCashfreeCardsQuery = (enabled = true) => {
  const accountId = getAccountId()
  return useQuery({
    queryKey: billingQueryKeys().cashfreeCards(accountId),
    queryFn: () => billingService.cashfreeListCards(accountId),
    enabled: enabled && !!accountId,
  })
}

export const useCashfreeVbanksQuery = (enabled = true, limit = 10, offset = 0) => {
  const accountId = getAccountId()
  return useQuery({
    staleTime: 0,
    enabled: enabled && !!accountId,
    queryKey: billingQueryKeys().cashfreeVbanks(accountId, limit, offset),
    queryFn: async () => {
      const response = await billingService.cashfreeListVbanks(accountId, limit, offset)
      return response.virtual_accounts
    },
  })
}

export const useCashfreeVbankPaymentsQuery = (
  vbankId: string,
  limit = 20,
  offset = 0,
  options?: { refetchInterval?: number; enabled?: boolean },
) =>
  useQuery({
    staleTime: 0,
    enabled: !!vbankId && options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    queryKey: billingQueryKeys().cashfreeVbankPayments(vbankId, limit, offset),
    queryFn: async () => {
      const response = await billingService.cashfreeGetVbankPayments(vbankId, limit, offset)
      return response.payments
    },
  })

export const useCashfreeSubscriptionsQuery = (limit = 10, offset = 0) => {
  const accountId = getAccountId()
  return useQuery({
    staleTime: 0,
    enabled: !!accountId,
    queryKey: billingQueryKeys().cashfreeSubscriptions(accountId, limit, offset),
    queryFn: async () => {
      const response = await billingService.cashfreeListSubscriptions(accountId, limit, offset)
      return response.subscriptions
    },
  })
}

// ============================================
// Cashfree Mutations
// ============================================

export const useCashfreeCreateOrderMutation = () =>
  useMutation({
    mutationFn: async (payload: CashfreeOrderCreatePayload) =>
      billingService.cashfreeCreateOrder(payload),
  })

export const useCashfreeVerifyPaymentMutation = () =>
  useMutation({
    mutationFn: async (orderId: string) => billingService.cashfreeVerifyPayment(orderId),
  })

// S2S /pay — covers new-card-save and saved-card-charge. Caller handles the
// payment_url redirect.
export const useCashfreePayMutation = () =>
  useMutation({
    mutationFn: async (payload: CashfreePayPayload) => billingService.cashfreePay(payload),
  })

// ── Razorpay (India PSP) mutations ────────────────────────────────────────
export const useRazorpayCreateOrderMutation = () =>
  useMutation({
    mutationFn: async (payload: RazorpayOrderCreatePayload) =>
      billingService.razorpayCreateOrder(payload),
  })

export const useRazorpayVerifyPaymentMutation = () =>
  useMutation({
    mutationFn: async (payload: RazorpayVerifyPayload) =>
      billingService.razorpayVerifyPayment(payload),
  })

// Charge a saved (tokenised) Razorpay card server-to-server. Used by the Custom
// Checkout modal's saved-card path — Custom Checkout has no hosted surface that
// could show the vaulted card, so the charge cannot go through razorpay.js.
export const useRazorpayPayWithTokenMutation = () =>
  useMutation({
    mutationFn: async (payload: RazorpayPayWithTokenPayload) =>
      billingService.razorpayPayWithToken(payload),
  })

// Delete a saved Razorpay card via the dedicated token_id route (de-tokenises at
// Razorpay + removes the local mapping). The token_id is the card's
// payment_intent_id from the shared /account/card list. Invalidates the shared
// cards list so the UI reflects the removal.
export const useRazorpayDeleteCardMutation = () => {
  const queryClient = useQueryClient()
  const accountId = getAccountId()
  return useMutation({
    mutationFn: async (tokenId: string) => billingService.razorpayDeleteCard(accountId, tokenId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingQueryKeys().cards(accountId) })
      successToast({ title: 'Success', description: 'Card deleted successfully' })
    },
    onError: () => {
      errorToast({ title: 'Failed', description: 'Failed to delete card. Please try again.' })
    },
  })
}

// ── Razorpay virtual account (bank transfer) ──────────────────────────────
export const useRazorpayCreateVbankMutation = () =>
  useMutation({
    mutationFn: async (payload: RazorpayVBankCreatePayload) =>
      billingService.razorpayCreateVbank(payload),
  })

export const useRazorpayVbankQuery = (vbankId: string, enabled = true) =>
  useQuery({
    staleTime: 0,
    enabled: enabled && !!vbankId,
    queryKey: billingQueryKeys().razorpayVbank(vbankId),
    queryFn: () => billingService.razorpayGetVbank(vbankId),
  })

export const useRazorpayVbankPaymentsQuery = (
  vbankId: string,
  options?: { refetchInterval?: number; enabled?: boolean },
) =>
  useQuery({
    staleTime: 0,
    enabled: !!vbankId && options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
    queryKey: billingQueryKeys().razorpayVbankPayments(vbankId),
    queryFn: async () => {
      const response = await billingService.razorpayGetVbankPayments(vbankId)
      return response.payments
    },
  })

// payment-service answers /razorpay/vbank/create with the account's virtual
// account, whether it already had one or was just provisioned. A response
// without a usable id is the one case this side still has to reject: the
// bank-transfer drawer renders the account number straight off it, so an empty
// id opens the drawer on a blank payee. Throwing lets the caller's catch turn it
// into a toast instead.
//
// Pure, so it can be tested without React or the shared billingService
// singleton (which sibling billing tests mock process-globally).
export const requireVbankId = (created?: { vbank_id?: string } | null): string => {
  if (!created?.vbank_id) {
    throw new Error('Razorpay vbank create returned no vbank_id')
  }
  return created.vbank_id
}

// Returns the account's usable Razorpay virtual account id.
//
// One call, every time: /razorpay/vbank/create is an ensure endpoint —
// payment-service persists the account's vbank_id, asks Razorpay for its live
// status, and either hands the same one back or provisions a replacement. The
// console used to keep the id in localStorage and do that reconciliation
// itself, which lost the mapping on every cache clear or new device and minted
// a second permanent VA each time, changing bank details the customer may have
// saved as a payee.
//
// Deliberately no caching here. Razorpay closes a virtual account after 90 days
// of inactivity, and that clock resets on every use, so freshness cannot be
// derived on this side -- the server has to be asked.
export const useEnsureActiveRazorpayVbank = () => {
  const accountId = getAccountId()
  const { mutateAsync: createVbank } = useRazorpayCreateVbankMutation()

  return async (): Promise<string> =>
    requireVbankId(await createVbank({ account_id: accountId, receiver_types: ['bank_account'] }))
}

export const useCashfreeCreateVbankMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CashfreeVBankCreatePayload) =>
      billingService.cashfreeCreateVbank(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingQueryKeys().cashfreeVbanksAll() })
    },
  })
}

// Ensures the account has an ACTIVE Cashfree virtual account before the
// read-only bank-transfer drawer opens, creating one (via the /in/vbank/create
// alias, hodor-proxied) only when none is active yet. Idempotent — a no-op when
// an active vbank already exists. Shared by the recharge modal and the settings
// "View Details" entry point so their provisioning logic can't drift.
export const useEnsureActiveVbank = () => {
  const accountId = getAccountId()
  const queryClient = useQueryClient()
  const { mutateAsync: createVbank } = useCashfreeCreateVbankMutation()

  return async (expectedAmount: number) => {
    const vbanksKey = billingQueryKeys().cashfreeVbanks(accountId, 10, 0)
    const cached =
      queryClient.getQueryData<
        Awaited<ReturnType<typeof billingService.cashfreeListVbanks>>['virtual_accounts']
      >(vbanksKey)
    const vbanks =
      cached ??
      (await queryClient.fetchQuery({
        queryKey: vbanksKey,
        queryFn: () =>
          billingService.cashfreeListVbanks(accountId, 10, 0).then((r) => r.virtual_accounts),
      }))
    if (vbanks?.some((vb) => vb.status === 'ACTIVE')) return
    await createVbank({
      account_id: accountId,
      expected_amount: expectedAmount,
      expiry_days: 365,
      product_type: 'plivo',
    })
  }
}

export const useCashfreeCreateSubscriptionMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CashfreeSubscriptionCreatePayload) =>
      billingService.cashfreeCreateSubscription(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingQueryKeys().cashfreeSubscriptionsAll() })
    },
  })
}

export const useCashfreeCancelSubscriptionMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (subscriptionId: string) =>
      billingService.cashfreeCancelSubscription(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingQueryKeys().cashfreeSubscriptionsAll() })
    },
  })
}

export const useCashfreePauseSubscriptionMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (subscriptionId: string) =>
      billingService.cashfreePauseSubscription(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingQueryKeys().cashfreeSubscriptionsAll() })
    },
  })
}

export const useCashfreeResumeSubscriptionMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (subscriptionId: string) =>
      billingService.cashfreeResumeSubscription(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingQueryKeys().cashfreeSubscriptionsAll() })
    },
  })
}
